/**
 * Recovery tracker — pure helpers. A curated protocol checklist (ice bath,
 * sauna, massage, …) checked off daily; XP is daily-capped adherence
 * (done / scheduled), farm-proof. `sumRecoveryXP` is the single definition,
 * crediting the Personal domain once + the Recovery area rank.
 */

export interface RecoveryProtocol {
  id: string;
  name: string;
  icon: string;
  active: boolean;
}

export interface RecoveryDay {
  date: string; // local YYYY-MM-DD
  doneIds: string[];
  scheduled: number;
}

import { POINTS } from './levelCalculation';

/** Icon keys offered by the protocol icon picker (mapped to lucide in the UI). */
export const RECOVERY_ICON_KEYS = [
  'snowflake', 'flame', 'waves', 'wind', 'droplet', 'footprints', 'hand', 'activity',
  'timer', 'bath', 'armchair', 'bed', 'stretch', 'heartpulse', 'sparkles', 'sun',
] as const;

export const DEFAULT_RECOVERY_ICON = 'sparkles';

/** Economy XP for a fully-adherent recovery day (matches the other trackers). */
export const DAILY_RECOVERY_XP = 50;

/** Seeded on first use; fully user-editable. */
export const DEFAULT_PROTOCOLS: RecoveryProtocol[] = [
  { id: 'rec-ice', name: 'Ice bath', icon: 'snowflake', active: true },
  { id: 'rec-sauna', name: 'Sauna', icon: 'flame', active: true },
  { id: 'rec-massage', name: 'Massage', icon: 'hand', active: true },
  { id: 'rec-stretch', name: 'Stretching', icon: 'stretch', active: true },
  { id: 'rec-foam', name: 'Foam rolling', icon: 'activity', active: true },
];

/** 0..100 adherence for a (done, scheduled) pair. */
export function recoveryAdherence(done: number, scheduled: number): number {
  if (scheduled <= 0) return 0;
  return Math.min(100, Math.max(0, (done / scheduled) * 100));
}

/** Economy XP for ONE day — proportional to adherence, capped at 100%. */
export function dayRecoveryXP(doneCount: number, scheduled: number): number {
  if (scheduled <= 0) return 0;
  // Config-backed cap (console → XP economy); the const above is the default.
  return Math.round(Math.min(1, Math.max(0, doneCount) / scheduled) * (POINTS.TRACKER_RECOVERY_DAILY ?? DAILY_RECOVERY_XP));
}

/** Total Recovery economy XP across days (single source of truth). */
export function sumRecoveryXP(days: ReadonlyArray<RecoveryDay>): number {
  return days.reduce((sum, d) => sum + dayRecoveryXP(d.doneIds?.length ?? 0, d.scheduled), 0);
}

/** A day is "fully recovered" once every scheduled protocol is done. */
export function isFullyRecovered(day: RecoveryDay): boolean {
  return day.scheduled > 0 && (day.doneIds?.length ?? 0) >= day.scheduled;
}

/**
 * Consecutive fully-adherent days ending at `todayStr`. An incomplete today
 * doesn't break the streak (mirrors the other trackers).
 */
export function recoveryStreak(
  days: ReadonlyArray<RecoveryDay>,
  todayStr: string,
  shift: (ymd: string, n: number) => string
): number {
  const full = new Map<string, boolean>();
  for (const d of days) full.set(d.date, isFullyRecovered(d));

  let streak = 0;
  let cursor = full.get(todayStr) ? todayStr : shift(todayStr, -1);
  while (full.get(cursor)) {
    streak++;
    cursor = shift(cursor, -1);
  }
  return streak;
}
